import { ActionListItem, DropOverActionList } from "client/Components";
import { ContactListItem } from "client/Api/Email/contacts";
import { useCommunicationActions } from "client/Features/AddressBook/useCommunicationActions";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import {
	ReactElement,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { ActionButton } from "./ActionButton";
import { getComputedDisplayName } from "client/Features/AddressBook/common";

export type ResponsiveCommunicationActionsProps = {
	contact: ContactListItem;
};

export const ResponsiveCommunicationActions = (
	props: ResponsiveCommunicationActionsProps
): ReactElement | null => {
	const {
		contact
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const buttonRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const {
		sendEmail,
		makePhoneCall,
		startTeamsChat,
		startTeamsVideoCall
	} = useCommunicationActions(contact);

	const doesSupportAnyAction: boolean = isNotNil(sendEmail)
		|| isNotNil(makePhoneCall)
		|| isNotNil(startTeamsChat)
		|| isNotNil(startTeamsVideoCall);

	const toggleMenuClickHandler = useCallback(
		(): void => {
			setIsOpen(x => !x);
		},
		[]
	);

	const closeHandler = useCallback(
		(): void => {
			setIsOpen(false);
		},
		[]
	);

	if (!doesSupportAnyAction) {
		return null;
	}

	return (
		<>
			<ActionButton
				icon="ellipsis"
				position="last"
				onClick={toggleMenuClickHandler}
				shouldOnlyBeVisibleOnHover={!isOpen}
				trackingId="collapsed-actions"
				ref={buttonRef}
				isActive={isOpen}
			/>
			{isOpen && buttonRef.current && (
				<DropOverActionList
					target={buttonRef.current}
					onClose={closeHandler}
					targetAlignment="br"
					contentAlignment="tr"
					trackingId={generateTrackingId("collapsed-actions", "__drop-over")}
					menuTitle={
						getComputedDisplayName(contact)
							.computedDisplayName
					}
				>
					{isNotNil(sendEmail) && (
						<ActionListItem
							icon="envelope"
							text={localize("_SendEmail")}
							onClick={sendEmail}
							trackingId="send-email"
						/>
					)}
					{isNotNil(makePhoneCall) && (
						<ActionListItem
							icon="phone"
							text={localize("_StartCall")}
							onClick={makePhoneCall}
							trackingId="make-phone-call"
						/>
					)}
					{isNotNil(startTeamsVideoCall) && (
						<ActionListItem
							icon="video"
							text={localize("_StartVideoChat")}
							onClick={startTeamsVideoCall}
							trackingId="start-teams-video-call"
						/>
					)}
					{isNotNil(startTeamsChat) && (
						<ActionListItem
							icon="comment"
							text={localize("_StartChat")}
							onClick={startTeamsChat}
							trackingId="start-teams-chat"
						/>
					)}
				</DropOverActionList>
			)}
		</>
	);
};
ResponsiveCommunicationActions.displayName = "ResponsiveCommunicationActions";